// packages/core/src/learning/core-modules/money-buys-options.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 01: "Money Buys Options" (tier: orientation)
// 5 lessons + checkpoint quiz + final test. Authored from generator content,
// assembled to the real Lesson / ModuleAssessment / LearningModule types.
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: `money-buys-options--money-is-options`,
	title: `Money is not just spending power`,
	description: `Money is much more than the ability to afford things. Money buys options. It gives you breathing room, more control over your time, and the ability to make choices before life makes them for you.`,
	pillar: `money-options`,
	schoolsOfThought: [
		`personal-finance-basics`,
		`life-design`,
		`money-psychology-housel`,
	],
	estimatedReadMinutes: 3,
	estimatedListenMinutes: 3,
	difficulty: 1,
	tags: [`money-basics`, `options`, `freedom`, `mindset`],
	prerequisites: [],
	relatedLessons: [`money-buys-options--runway-beats-panic`],
	concepts: [
		`money-as-options`,
		`breathing-room`,
		`choice`,
		`future-control`,
	],
	blocks: [
		{
			type: `paragraph`,
			text: `When people first think about money, they usually think about what it can buy. A phone. A car. A better apartment. A night out. A trip. Those things matter, but that's just the surface.`,
		},
		{
			type: `paragraph`,
			text: `Even a small amount of money saved can have an impact.`,
		},
		{
			type: `paragraph`,
			text: `Often, that impact is overlooked. Even a single dollar can be the difference between a payment going through and your account falling below zero. That dollar can be what protects you from an overdraft fee or a declined payment. In either situation, you'd end up having to scramble for extra cash.`,
		},
		{
			type: `paragraph`,
			text: `For some people, this is their first step toward real financial change. It's not a large savings account or a life-changing amount of money. Instead, it is simply having enough left over to stop one "small" problem from turning into three.`,
		},
		{
			type: `paragraph`,
			text: `That's the breathing room money can give you. In other words, money can buy you options. Options like having more control over your time and finally being able to make choices that life used to make for you.`,
		},
		{
			type: `paragraph`,
			text: `At first, these options seem small. Paying a surprise bill without borrowing money. Making it to payday without checking your bank balance a hundred times. It could mean handling a car repair you would otherwise have to put off.`,
		},
		{
			type: `paragraph`,
			text: `This is where the breathing room people fought to create either disappears or becomes the foundation for something much more significant: something that grows into lasting financial change. Once people have a little extra money, their spending often grows to meet it. The breathing room disappears before it ever has the chance to grow.`,
		},
		{
			type: `paragraph`,
			text: `There are always more things to buy, more ways to upgrade your life, and more reasons to tell yourself that you will start saving next time. The goal isn't to hold onto every dollar or remove everything enjoyable from your life. The goal is far simpler than that. You just have to see that keeping some money is valuable in and of itself. The more breathing room you protect, the less often life gets to make every decision for you.`,
		},
		{
			type: `key_takeaway`,
			text: `Money buys options. The more breathing room you protect, the more choices stay yours.`,
		},
	],
	audioScript: `Think about the last time money felt tight. Maybe you were waiting for payday. Maybe a bill hit earlier than expected. Maybe your balance was so close to zero that one small charge could have changed the rest of your week.

That is where the idea behind this lesson becomes real. Money is not only useful when you spend it. Sometimes its most important job is simply being there.

A few dollars left in your account might keep a payment from failing. A few hundred dollars might cover a repair without forcing you to borrow. A larger cushion might give you time to look for a better job instead of taking the first thing available because rent is due. The amount changes, but the purpose stays the same. Money creates space between what happens and what you have to do next.

That space is easy to underestimate because it does not always look exciting. You cannot post a picture of an overdraft fee that never happened. Nobody congratulates you because your car repair was annoying instead of terrifying. Breathing room is mostly invisible. You notice it through the panic that does not arrive and the bad option you do not have to take.

The hard part is protecting it. Once there is a little extra money, there is always somewhere for it to go. A better phone. More takeout. Another subscription. A nicer version of something you already own. None of those things are automatically bad. The problem begins when every increase in income is followed by an equal increase in spending. The space disappears before it can become anything larger.

You do not need to save every dollar or turn your life into a punishment. That misses the point. The point is to recognize that money sitting still can still be working for you. It can protect your time. It can lower the pressure behind your decisions. It can stop one problem from creating two more.

That is the first shift Nisk wants you to make. Do not only ask what your money can buy today. Ask what keeping some of it available might allow you to choose tomorrow.`,
	flashcards: [
		{
			id: `money-buys-options--money-is-options--fc-main`,
			role: `main_idea`,
			front: `What can saved money give you besides buying power?`,
			back: `Saved money can give you breathing room, time, and more control over what happens next.`,
		},
		{
			id: `money-buys-options--money-is-options--fc-watch`,
			role: `watch_out`,
			front: `How can early breathing room disappear?`,
			back: `It can disappear when spending grows every time a little extra money becomes available.`,
		},
		{
			id: `money-buys-options--money-is-options--fc-use`,
			role: `use_it`,
			front: `What should you notice about money you keep?`,
			back: `Keeping some money available has value because it protects future choices.`,
		},
	],
	applyAction: {
		type: `openDashboard`,
		label: `See your current money picture`,
	},
	revision: 3,
};

const LESSON_2: Lesson = {
	id: `money-buys-options--runway-beats-panic`,
	title: `Runway beats panic`,
	description: `Runway measures how long your money can keep your basic life running when income slows down or stops.`,
	pillar: `money-options`,
	schoolsOfThought: [
		`personal-finance-basics`,
		`risk-management`,
		`life-design`,
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 3,
	difficulty: 1,
	tags: [`runway`, `emergency-fund`, `stability`, `options`],
	prerequisites: [`money-buys-options--money-is-options`],
	relatedLessons: [`money-buys-options--money-is-options`],
	concepts: [
		`financial-runway`,
		`cash-buffer`,
		`decision-time`,
		`stability-before-speed`,
	],
	blocks: [
		{
			type: `paragraph`,
			text: `Breathing room becomes easier to understand when you measure it in time. That time is your runway. Runway is how long your available money could keep your basic life running if your income slowed down, stopped, or arrived later than expected.`,
		},
		{
			type: `paragraph`,
			text: `If your basic monthly expenses are $2,000 and you have $2,000 available, you have about one month of runway. If you have $500, you have roughly one week. Neither amount makes you rich. Both amounts tell you how much time stands between an income problem and a much larger emergency.`,
		},
		{
			type: `paragraph`,
			text: `That time matters because life does not wait for your finances to become perfect. Work hours get cut. Cars break down. A family member needs help. A paycheck arrives late. A job becomes unbearable. When there is no runway, the problem and the panic arrive together.`,
		},
		{
			type: `callout`,
			icon: `🛟`,
			text: `Runway does not prevent problems. It gives you more time and better options when they happen.`,
		},
		{
			type: `paragraph`,
			text: `People sometimes treat cash savings as wasted potential because the money is not growing quickly or buying anything visible. But that money has a different job. Its job is to keep a setback from forcing you into an expensive or rushed decision.`,
		},
		{
			type: `example`,
			label: `Same repair, different pressure`,
			text: `Two people both need a $700 car repair. One has $75 available and puts the repair on a credit card while hoping the next paycheck covers everything else. The other has $1,000 saved. The repair still hurts, but it gets handled without creating a new monthly payment. The bill is the same. The pressure is not.`,
		},
		{
			type: `paragraph`,
			text: `You do not need six months of expenses before your runway begins to matter. The first $250 matters. So does $500. Then one month. Each layer creates a little more distance between a bad surprise and a desperate choice.`,
		},
		{
			type: `paragraph`,
			text: `Runway can also change decisions that are not emergencies. It can give you time to compare job offers, leave a harmful situation, move with a plan, or say no to something that only works because you feel trapped. The money is not making the decision for you. It is giving you more room to make it yourself.`,
		},
		{
			type: `paragraph`,
			text: `The goal is not to eliminate uncertainty. No amount of savings can make life fully predictable. The goal is to become less fragile. A problem may still knock you backward, but it does not have to knock everything down with it.`,
		},
		{
			type: `key_takeaway`,
			text: `Runway turns money into time, and time gives you more control over your next move.`,
		},
	],
	audioScript: `Runway is just breathing room measured in time.

Here is the easiest way to picture it. Imagine your income stopped for a moment. Maybe your hours were cut. Maybe a paycheck was delayed. Maybe you needed to leave a job before another one was ready. How long could your available money keep the basics going?

That amount of time is your runway.

If your basic expenses are around two thousand dollars a month and you have two thousand dollars saved, that is about one month. If you have five hundred dollars, that might be about a week. A week may not sound like much, but compare it with having no time at all. One week can be enough to avoid a late fee, wait for the next paycheck, or make a decision without everything collapsing at once.

This is why cash can be valuable even when it looks like it is doing nothing. Its job is not always growth. Sometimes its job is protection.

Think about a seven hundred dollar car repair. Without savings, the repair may become credit card debt. Then the repair is no longer only seven hundred dollars. It becomes interest, another minimum payment, and less money available next month. With a cushion, the repair still hurts, but it can end there. You pay it, rebuild, and move forward.

That is what runway changes. It limits how far one problem gets to spread.

There is no single amount that makes everyone safe. Someone living with family may need less than someone paying rent and supporting a child. Someone with steady work may think about runway differently from someone whose income changes every month. The exact number depends on the life around it.

But the direction is the same. First, create a little space. Then protect it. Then slowly extend it.

Runway is not exciting in the way a purchase is exciting. Most of the time, it is quiet. But when life gets loud, that quiet money can become time, patience, and the ability to choose your next move instead of having it chosen for you.`,
	flashcards: [
		{
			id: `money-buys-options--runway-beats-panic--fc-main`,
			role: `main_idea`,
			front: `What does financial runway measure?`,
			back: `It measures how long available money could keep your basic life running if income were interrupted.`,
		},
		{
			id: `money-buys-options--runway-beats-panic--fc-watch`,
			role: `watch_out`,
			front: `Why is cash not automatically wasted money?`,
			back: `Cash can prevent a setback from turning into debt, panic, or a rushed decision.`,
		},
		{
			id: `money-buys-options--runway-beats-panic--fc-use`,
			role: `use_it`,
			front: `How can you begin thinking about runway?`,
			back: `Compare your available cash with the cost of keeping your basic life running.`,
		},
	],
	applyAction: {
		type: `calculateRunway`,
		label: `Check your current runway`,
	},
	revision: 2,
};

const LESSON_3: Lesson = {
	id: `money-buys-options--every-choice-has-a-receipt`,
	title: `Every choice has a receipt`,
	description: `Every purchase has an obvious price and a quieter price: the other option that money can no longer support.`,
	pillar: `money-options`,
	schoolsOfThought: [`behavioral-finance`, `frugality`, `life-design`],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 3,
	difficulty: 1,
	tags: [`opportunity-cost`, `spending`, `tradeoffs`, `choices`],
	prerequisites: [`money-buys-options--runway-beats-panic`],
	relatedLessons: [`money-buys-options--small-money-still-counts`],
	concepts: [
		`opportunity-cost`,
		`hidden-price`,
		`intentional-spending`,
		`values-tradeoff`,
	],
	blocks: [
		{
			type: `paragraph`,
			text: `Every money choice has two prices. The first is easy to see. It is the amount on the receipt or the charge in your bank account. The second price is quieter. It is whatever that money can no longer do once you spend it.`,
		},
		{
			type: `paragraph`,
			text: `Finance calls this opportunity cost. The name sounds technical, but the idea is simple. When money goes toward one option, it becomes unavailable for another. A dollar cannot buy dinner tonight and still be available for a repair next month.`,
		},
		{
			type: `paragraph`,
			text: `This idea is easy to misuse. You can turn it into guilt and start treating every coffee, meal, trip, or hobby as a mistake. That is not the point. Spending can make life easier, richer, and more enjoyable. The point is to understand the full trade before you make it.`,
		},
		{
			type: `callout`,
			icon: `🧾`,
			text: `The question is not "Is this purchase bad?" The question is "What am I trading for it?"`,
		},
		{
			type: `paragraph`,
			text: `Sometimes the trade is clearly worth it. A meal with someone you love may matter more than keeping the money. Paying for convenience may protect your time during a difficult week. A safer apartment may cost more and still improve your life. The cheapest choice is not automatically the best choice.`,
		},
		{
			type: `paragraph`,
			text: `The problems usually come from trades you never meant to make. A subscription renews for months. Delivery fees become routine. A raise disappears into small upgrades. Then, when you want to move, fix your car, visit family, or leave a job, the money is gone and you are not sure what you received in return.`,
		},
		{
			type: `example`,
			label: `The quieter price`,
			text: `Suppose $120 disappears each month across subscriptions, delivery fees, and purchases you barely remember. That becomes $1,440 over a year. The spending is not automatically wrong. But that same $1,440 could also become runway, part of a move, a repair fund, or one larger experience you care about much more.`,
		},
		{
			type: `paragraph`,
			text: `The goal is not to calculate the future value of every dollar before you enjoy your life. That would be exhausting. You only need to start noticing when a repeated expense is quietly competing with something more important to you.`,
		},
		{
			type: `bullet_list`,
			items: [
				`Notice the price you can see`,
				`Name the option you are giving up`,
				`Decide whether the trade still feels worth it`,
				`Move forward without guilt`,
			],
		},
		{
			type: `paragraph`,
			text: `A conscious purchase can still be expensive. The difference is that it has a purpose. "I chose this because it matters to me" feels very different from "I have no idea where my money went."`,
		},
		{
			type: `key_takeaway`,
			text: `Spending is not the enemy. The danger is giving up options without noticing the trade.`,
		},
	],
	audioScript: `Every purchase has the price you see and the price you usually do not.

The visible price is simple. Twenty dollars for lunch. Eighty dollars for a subscription. Four hundred dollars for a car payment. The quieter price is the other thing that money could have supported.

That is opportunity cost. It sounds like finance language, but it is really just a reminder that the same money cannot do two jobs at once.

Now, this idea can become unhealthy fast if you use it to attack yourself. You can make every meal out feel irresponsible. You can make every trip feel like a mistake. You can convince yourself that enjoying money today means betraying your future. That is not what this lesson is asking you to do.

Sometimes spending is exactly what you want your money to do. A good meal can be worth it. Paying for convenience can be worth it. A hobby can make your life better. A safer place to live may be worth more than the extra money it costs.

The goal is not to choose the cheapest option every time. The goal is to notice the trade.

Imagine one hundred twenty dollars leaves your account every month through a mix of apps, delivery fees, and subscriptions you barely use. Over a year, that is more than fourteen hundred dollars. Maybe you love every one of those purchases. Fine. Keep them. But maybe you would rather have that money available for a move, a repair, a trip, or a little more runway.

The important part is that you see the choice while you can still make it.

A lot of money frustration is really a mismatch between what someone says matters and where their money quietly goes. The individual purchases may not look serious. The pattern is what eventually crowds out the bigger option.

So when a repeated cost shows up, ask one clean question: what am I trading for this? Not, is this bad? Not, should I feel guilty? Just, does this trade match what I actually care about?

That question will not remove every bad purchase. It will do something more useful. It will make more of your spending feel chosen.`,
	flashcards: [
		{
			id: `money-buys-options--every-choice-has-a-receipt--fc-main`,
			role: `main_idea`,
			front: `What is the quieter price of a purchase?`,
			back: `It is the other option that money can no longer support after it is spent.`,
		},
		{
			id: `money-buys-options--every-choice-has-a-receipt--fc-watch`,
			role: `watch_out`,
			front: `How can opportunity cost become unhealthy?`,
			back: `It becomes unhealthy when you use it to feel guilty about every purchase.`,
		},
		{
			id: `money-buys-options--every-choice-has-a-receipt--fc-use`,
			role: `use_it`,
			front: `What question makes a tradeoff visible?`,
			back: `Ask what you are trading for the purchase and whether that trade still feels worth it.`,
		},
	],
	applyAction: {
		type: `noToolAction`,
		label: `Name the second price`,
		prompt: `Choose one repeated expense and name what that money could support instead.`,
	},
	revision: 2,
};

const LESSON_4: Lesson = {
	id: `money-buys-options--small-money-still-counts`,
	title: `Small money still counts`,
	description: `Modest amounts become meaningful when they repeat, remain protected, and begin changing the way ordinary problems affect you.`,
	pillar: `money-options`,
	schoolsOfThought: [`personal-finance-basics`, `first-100k`, `fi-math`],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 3,
	difficulty: 1,
	tags: [`small-wins`, `savings`, `consistency`, `first-100k`],
	prerequisites: [`money-buys-options--every-choice-has-a-receipt`],
	relatedLessons: [`money-buys-options--future-self-needs-a-vote`],
	concepts: [
		`small-progress`,
		`repetition`,
		`protected-savings`,
		`financial-momentum`,
	],
	blocks: [
		{
			type: `paragraph`,
			text: `A lot of people dismiss small amounts before they ever give them a chance to matter. Twenty-five dollars feels too small. One hundred dollars feels too far from a major goal. When rent, debt, groceries, and car costs are large, modest progress can feel almost pointless.`,
		},
		{
			type: `paragraph`,
			text: `But small money does not need to solve your entire life to be useful. It only needs to change the next thing. Fifty dollars might keep your account positive. Two hundred dollars might cover a bill. Five hundred dollars might keep a repair off a credit card.`,
		},
		{
			type: `paragraph`,
			text: `The mistake is comparing your first layer of progress with somebody else's finished result. A small amount looks weak beside a paid-off house or a six-month emergency fund. It looks very different beside zero.`,
		},
		{
			type: `callout`,
			icon: `🌱`,
			text: `Small money becomes powerful through repetition, not through one dramatic moment.`,
		},
		{
			type: `example`,
			label: `A pattern begins`,
			text: `Saving $100 a month creates $600 in six months and $1,200 in one year. That may become a starter emergency fund, part of a move, or enough to handle several smaller problems without borrowing. The amount is useful, but the repeatable pattern is what makes it grow.`,
		},
		{
			type: `paragraph`,
			text: `Small progress also changes what you believe about yourself. The first amount you keep proves that not every dollar has to disappear. The second proves it was not an accident. Over time, saving stops feeling like something other people do and starts becoming part of how you handle money.`,
		},
		{
			type: `paragraph`,
			text: `That does not mean consistency is always easy. Some months will cost more. Income can change. Family may need help. An emergency may use the money you worked hard to save. Using savings for the reason you built them is not losing progress. It is the progress doing its job.`,
		},
		{
			type: `paragraph`,
			text: `Small amounts can also reveal where the real pressure is. If keeping even $25 feels impossible, that tells you something. Income may be too tight. Debt may be consuming the gap. Spending may be higher than you realized. The answer is not shame. The answer is a clearer picture of what needs attention.`,
		},
		{
			type: `bullet_list`,
			items: [
				`Keep the first amount you can`,
				`Protect it from automatic spending`,
				`Repeat it when life allows`,
				`Let the pattern grow before judging it`,
			],
		},
		{
			type: `paragraph`,
			text: `You are not pretending $25 is a fortune. You are recognizing that every larger layer of financial stability begins with money that once looked too small to matter.`,
		},
		{
			type: `key_takeaway`,
			text: `Small money counts when it repeats, stays protected, and changes what the next problem can do to you.`,
		},
	],
	audioScript: `Small amounts are easy to disrespect.

You look at twenty-five dollars and compare it with rent. You look at one hundred dollars and compare it with a car repair. You look at five hundred dollars and compare it with the amount you think you are supposed to have saved. Of course it feels small.

But that comparison misses the job of early progress.

Your first saved money does not need to solve everything. It needs to change something. Maybe it keeps your account from going negative. Maybe it covers a phone bill. Maybe it means a small emergency does not have to land on a credit card. That is already different from having nothing available.

The other reason small money matters is that it can repeat. One hundred dollars once is useful. One hundred dollars every month becomes six hundred dollars in half a year and twelve hundred dollars in a year. The first amount is not powerful because it is huge. It is powerful because it can become a pattern.

That pattern also changes your confidence. The first month proves you can keep something. The next month proves it was not random. Eventually, you stop seeing every extra dollar as temporary spending money. Some of it begins to belong to your future before anything else claims it.

There will be interruptions. You may save for months and then use most of it on a repair. That can feel like going backward, but it is not. The savings protected you from a worse outcome. That was the job.

And if saving a small amount feels impossible, do not turn that into a judgment about yourself. Treat it as information. Maybe the income side is too tight. Maybe debt payments are taking the room. Maybe several smaller expenses are adding up. Now you know where the pressure lives.

Small money is not magic. It is not a substitute for higher income or lower costs when those are truly needed. It is simply the first layer. Every larger amount was once a smaller amount that somebody kept long enough to grow.`,
	flashcards: [
		{
			id: `money-buys-options--small-money-still-counts--fc-main`,
			role: `main_idea`,
			front: `Why can a small saved amount still matter?`,
			back: `It can change the next problem and become meaningful when the pattern repeats.`,
		},
		{
			id: `money-buys-options--small-money-still-counts--fc-watch`,
			role: `watch_out`,
			front: `What comparison can make progress feel pointless?`,
			back: `Comparing your first small layer of savings with someone else's finished result.`,
		},
		{
			id: `money-buys-options--small-money-still-counts--fc-use`,
			role: `use_it`,
			front: `What should you do with an amount that feels small?`,
			back: `Protect it, repeat it when possible, and judge the pattern after it has time to grow.`,
		},
	],
	applyAction: {
		type: `openImpactScenario`,
		label: `See what $100 can become`,
		scenarioId: `hundred-first-job`,
	},
	revision: 2,
};

const LESSON_5: Lesson = {
	id: `money-buys-options--future-self-needs-a-vote`,
	title: `Your future self needs a vote`,
	description: `Healthy money decisions make room for your life today without allowing every future option to be spent in advance.`,
	pillar: `money-options`,
	schoolsOfThought: [
		`life-design`,
		`personal-finance-basics`,
		`rich-life-ramit`,
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 3,
	difficulty: 1,
	tags: [`future-self`, `values`, `spending`, `life-design`],
	prerequisites: [`money-buys-options--small-money-still-counts`],
	relatedLessons: [`money-buys-options--every-choice-has-a-receipt`],
	concepts: [
		`future-self`,
		`present-needs`,
		`intentional-balance`,
		`sustainable-choice`,
	],
	blocks: [
		{
			type: `paragraph`,
			text: `Money gets messy when only one version of you gets to vote. Present you wants relief, comfort, fun, convenience, and experiences that make life feel worth living. Future you wants savings, fewer bills, more runway, and choices that are not controlled by the next paycheck.`,
		},
		{
			type: `paragraph`,
			text: `Both versions are real. Ignoring present you can create a plan that feels miserable and eventually falls apart. Ignoring future you can make every month comfortable while the larger parts of life become harder to change.`,
		},
		{
			type: `paragraph`,
			text: `The challenge is that present you is louder. The purchase is here. The stress is here. The invitation is tonight. Future consequences usually arrive quietly and later, after the money has already left.`,
		},
		{
			type: `callout`,
			icon: `⚖️`,
			text: `A good money plan does not make you hate today. It keeps today from spending every option tomorrow may need.`,
		},
		{
			type: `paragraph`,
			text: `This is where values become more useful than guilt. Spending on something you care about can be a good use of money. Spending because you are stressed, bored, comparing yourself with someone else, or moving on autopilot may leave you with less money and very little memory of what it added to your life.`,
		},
		{
			type: `example`,
			label: `Give both sides a seat`,
			text: `Imagine you have an extra $150 this month. You could spend all of it, save all of it, or divide it. Maybe $75 pays for a night you will genuinely enjoy and $75 stays available as breathing room. There is no universal perfect split. The important part is that both your present life and your future options were considered.`,
		},
		{
			type: `paragraph`,
			text: `A common pattern is to swing between extremes. Someone spends freely, feels guilty, creates a harsh plan, follows it for a week, then breaks it and feels like they failed. The problem is not a lack of discipline. The system never made room for a real human life.`,
		},
		{
			type: `paragraph`,
			text: `A sustainable choice can be modest. Keep part of a raise instead of all of it. Enjoy one subscription and cancel the five you forgot about. Plan for a trip while still protecting a small cushion. The goal is not perfect balance every month. It is a pattern where your future is not always the side that loses.`,
		},
		{
			type: `bullet_list`,
			items: [
				`Name what present you wants`,
				`Name what future you may need`,
				`Choose the trade on purpose`,
				`Build a pattern you can actually repeat`,
			],
		},
		{
			type: `paragraph`,
			text: `Your future self is not a stranger who deserves everything. Your present self is not an enemy who deserves nothing. They are the same person at different points in time, and your money decisions connect them.`,
		},
		{
			type: `key_takeaway`,
			text: `The cleanest money choices support your life today without spending every option your future may need.`,
		},
	],
	audioScript: `Your future self is easy to ignore because they are not standing in front of you.

Present you is here right now. Present you is tired. Present you wants the meal, the trip, the upgrade, the easier option, or just a little relief after a long week. Those wants are real. A money plan that pretends they do not exist usually becomes a plan you cannot stand.

But future you is real too. Future you is the person who deals with the repair, the move, the job change, the debt payment, or the opportunity that needs money behind it. When every dollar is used to make today easier, future you receives fewer choices.

The answer is not to let future you control everything either. Saving every possible dollar while making your current life miserable is not balance. It can turn money into punishment, and punishment is hard to repeat for years.

Think of it as a vote. Present you gets a vote because life is happening now. Future you gets a vote because consequences arrive later. A healthy decision gives both sides a seat.

Say you have an extra one hundred fifty dollars this month. Maybe part of it pays for something you will truly enjoy. Maybe part stays in your account. There is no universal split that proves you are responsible. The useful part is that you made the trade consciously instead of letting the whole amount disappear by default.

A lot of people bounce between overspending and overcorrecting. They spend, feel bad, create a strict plan, break it, then spend again because the plan already feels ruined. That cycle is not a character flaw. It is often a sign that the plan did not include enough room for real life.

A better pattern is less dramatic. Keep some of a raise. Protect a small amount before upgrading everything else. Spend more on what you care about and less on what you barely notice. You are not trying to win every decision. You are trying to stop your future from losing every time.

Present you and future you are not opponents. They are the same person. Money simply decides how much support passes from one version to the other.`,
	flashcards: [
		{
			id: `money-buys-options--future-self-needs-a-vote--fc-main`,
			role: `main_idea`,
			front: `What does giving your future self a vote mean?`,
			back: `It means protecting some future options while still making room for life today.`,
		},
		{
			id: `money-buys-options--future-self-needs-a-vote--fc-watch`,
			role: `watch_out`,
			front: `Why can an extreme money plan fail?`,
			back: `A plan that ignores present needs can become miserable, break quickly, and restart the guilt cycle.`,
		},
		{
			id: `money-buys-options--future-self-needs-a-vote--fc-use`,
			role: `use_it`,
			front: `How can you make a more balanced choice?`,
			back: `Name what you want now, what you may need later, and choose the trade on purpose.`,
		},
	],
	applyAction: {
		type: `noToolAction`,
		label: `Give both versions of you a vote`,
		prompt: `Choose one money decision this month and write what present you wants, what future you may need, and what trade respects both.`,
	},
	revision: 2,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: `money-buys-options`,
	checkpointQuiz: [
		{
			type: `multiple_choice`,
			id: `money-buys-options--cq--q1`,
			question: `What is the main idea behind "money buys options"?`,
			options: [
				`Money is only useful when it is invested`,
				`Money gives you room to move and make better choices`,
				`Money should never be spent on fun`,
				`Money only matters once you earn a high income`,
			],
			correctIndex: 1,
			explanation: `The module frames money as breathing room, time, and choice, not only spending power.`,
		},
		{
			type: `multiple_choice`,
			id: `money-buys-options--cq--q2`,
			question: `Which example best shows money buying an option?`,
			options: [
				`Buying something only because your friend bought it`,
				`Saving enough to handle a car repair without panic`,
				`Ignoring your bills because planning feels stressful`,
				`Spending every raise before it reaches checking`,
			],
			correctIndex: 1,
			explanation: `A cushion creates room to handle the problem without immediately creating another one.`,
		},
		{
			type: `true_false`,
			id: `money-buys-options--cq--q3`,
			question: `Runway means the amount of time your money can keep your basic life running if income is interrupted.`,
			correct: true,
			explanation: `Runway compares available money with the cost of keeping basic expenses covered.`,
		},
		{
			type: `multiple_choice`,
			id: `money-buys-options--cq--q4`,
			question: `Why can cash runway be valuable even if it is not growing quickly?`,
			options: [
				`It removes every financial risk`,
				`It protects your ability to think clearly during setbacks`,
				`It replaces the need to earn income`,
				`It makes all spending decisions easy`,
			],
			correctIndex: 1,
			explanation: `Cash has a protection job. It can stop a setback from forcing panic, debt, or a rushed decision.`,
		},
		{
			type: `scenario`,
			id: `money-buys-options--cq--q5`,
			scenario: `Jordan has $900 saved and faces a $600 car repair. Their friend has no savings and puts the same repair on a high interest credit card.`,
			question: `What is the best lesson from this situation?`,
			options: [
				`Jordan is rich, so money is no longer a concern`,
				`The same emergency creates different pressure when one person has runway`,
				`Credit cards are always bad in every situation`,
				`Car repairs should never be paid in cash`,
			],
			correctIndex: 1,
			explanation: `The point is not moral judgment. Runway changes how far the same problem can spread.`,
		},
		{
			type: `scenario`,
			id: `money-buys-options--cq--q6`,
			scenario: `Maya spends $110 a month on apps and delivery fees she barely remembers. She wants to move out but feels stuck.`,
			question: `What should Maya notice first?`,
			options: [
				`The spending is automatically wrong`,
				`The monthly amount has no effect because it is too small`,
				`The spending has a quieter price if it competes with a goal she values more`,
				`She needs to remove all fun immediately`,
			],
			correctIndex: 2,
			explanation: `The lesson is about seeing the tradeoff, not feeling guilty about every purchase.`,
		},
		{
			type: `multiple_choice`,
			id: `money-buys-options--cq--q7`,
			question: `What is the healthiest way to use opportunity cost?`,
			options: [
				`Use it to shame yourself for every purchase`,
				`Compare what money supports now with the option it could protect later`,
				`Avoid all spending forever`,
				`Prove that small amounts never matter`,
			],
			correctIndex: 1,
			explanation: `Opportunity cost helps make a trade visible without automatically deciding that the purchase is wrong.`,
		},
	],
	finalTest: [
		{
			type: `multiple_choice`,
			id: `money-buys-options--final--q1`,
			question: `Which statement best captures Module 1?`,
			options: [
				`Money is mainly about looking successful`,
				`Money can create options, runway, and more control over your choices`,
				`Money only matters once you start investing`,
				`Money should be avoided because it causes stress`,
			],
			correctIndex: 1,
			explanation: `The module reframes money as breathing room and choice, not only consumption.`,
		},
		{
			type: `true_false`,
			id: `money-buys-options--final--q2`,
			question: `Spending is always the enemy of financial progress.`,
			correct: false,
			explanation: `The module supports conscious spending. It warns against giving up options without noticing the trade.`,
		},
		{
			type: `multiple_choice`,
			id: `money-buys-options--final--q3`,
			question: `What is the best definition of runway?`,
			options: [
				`How much money you earn before taxes`,
				`How long available money can keep your basic life running if income is interrupted`,
				`How much your investments may grow`,
				`How much credit you can access`,
			],
			correctIndex: 1,
			explanation: `Runway turns available money into an estimate of time and stability.`,
		},
		{
			type: `scenario`,
			id: `money-buys-options--final--q4`,
			scenario: `Alex saves $500 and says, "This is pointless. It is not enough to change my life."`,
			question: `What is the strongest response from this module?`,
			options: [
				`$500 never matters`,
				`$500 only matters if it is invested immediately`,
				`$500 may not solve everything, but it can change what the next problem does to Alex`,
				`Alex should spend it because the amount is too small`,
			],
			correctIndex: 2,
			explanation: `A small amount can still create breathing room and become the beginning of a repeatable pattern.`,
		},
		{
			type: `multiple_choice`,
			id: `money-buys-options--final--q5`,
			question: `What is the danger of seeing money only as spending power?`,
			options: [
				`You may ignore how keeping money available protects future options`,
				`You may save too much too quickly`,
				`You may never enjoy anything again`,
				`You may understand opportunity cost too well`,
			],
			correctIndex: 0,
			explanation: `Buying things is only one job money can do. It can also create time, room, and choices.`,
		},
		{
			type: `scenario`,
			id: `money-buys-options--final--q6`,
			scenario: `Sam gets an extra $150 this month. They want to enjoy part of it but also build breathing room.`,
			question: `Which choice best reflects the future self lesson?`,
			options: [
				`Spend all of it before thinking`,
				`Save all of it and feel resentful`,
				`Choose a split that supports life today and protects something for later`,
				`Ignore the money because $150 is too small`,
			],
			correctIndex: 2,
			explanation: `The lesson gives both present needs and future options a vote.`,
		},
		{
			type: `multiple_choice`,
			id: `money-buys-options--final--q7`,
			question: `What is the quieter price on a purchase?`,
			options: [
				`Sales tax`,
				`The other option that money can no longer support`,
				`The store name`,
				`The payment method`,
			],
			correctIndex: 1,
			explanation: `The quieter price is the tradeoff that becomes unavailable after the money is spent.`,
		},
		{
			type: `true_false`,
			id: `money-buys-options--final--q8`,
			question: `Small money can matter because repetition can turn a modest amount into useful breathing room.`,
			correct: true,
			explanation: `The module treats small progress as a first layer that can repeat, grow, and change future pressure.`,
		},
		{
			type: `scenario`,
			id: `money-buys-options--final--q9`,
			scenario: `Nina tries a strict plan with no room for enjoyment, breaks it after a week, then overspends because she feels like she already failed.`,
			question: `What is the module's best diagnosis?`,
			options: [
				`Nina needs more guilt`,
				`Nina needs a plan that protects future options while still making room for real life`,
				`Nina should ignore planning forever`,
				`Nina failed because small money does not matter`,
			],
			correctIndex: 1,
			explanation: `A plan that ignores present needs can become impossible to sustain.`,
		},
		{
			type: `multiple_choice`,
			id: `money-buys-options--final--q10`,
			question: `Which question best fits the module's decision style?`,
			options: [
				`Can I technically afford this right now?`,
				`What am I trading for this, and does that trade still feel worth it?`,
				`How do I copy what everyone else is doing?`,
				`What purchase will make me look most successful?`,
			],
			correctIndex: 1,
			explanation: `The question makes the tradeoff visible without automatically judging the choice.`,
		},
		{
			type: `scenario`,
			id: `money-buys-options--final--q11`,
			scenario: `Chris wants to start improving their money but can only set aside $25 this week.`,
			question: `What is the most useful interpretation?`,
			options: [
				`$25 is useless, so Chris should wait`,
				`$25 is only valuable if it produces a large return`,
				`$25 can be the first layer of a repeatable pattern`,
				`Chris should feel embarrassed`,
			],
			correctIndex: 2,
			explanation: `Small progress can matter before it becomes a large result.`,
		},
		{
			type: `multiple_choice`,
			id: `money-buys-options--final--q12`,
			question: `What is the main difference between conscious spending and money leakage?`,
			options: [
				`Conscious spending is chosen because it supports something you value, while leakage disappears without a clear purpose`,
				`Conscious spending is always cheap, while leakage is always expensive`,
				`Conscious spending only happens after you are rich`,
				`Leakage only means cash spending`,
			],
			correctIndex: 0,
			explanation: `The module does not say all spending is bad. It asks whether the money is doing a job you chose.`,
		},
	],
};

export const MONEY_BUYS_OPTIONS_MODULE: LearningModule = {
	id: `money-buys-options`,
	title: `Money Buys Options`,
	description: `Money is more than spending power. It means options, runway, and room to breathe. This is the orientation module the rest of the Core Path builds on.`,
	emoji: `🚪`,
	accentColor: `#c8ff00`,
	tier: `orientation`,
	pillar: `money-options`,
	order: 1,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
